import React, { useRef } from 'react';
import type { CheckboxProps, CommonInputProps } from './index';
import { Checkbox } from './index';
import type { ChildrenProp, ClassNameProp } from '../common-types';
import _ from 'lodash';
import { ValidationErrorMessage } from './internal';

export type CheckboxGroupProps = Pick<
  CommonInputProps<boolean>,
  'label' | 'error' | 'readOnly' | 'required'
> &
  Required<ChildrenProp> &
  ClassNameProp & {
    /**
     * If displayed as a row or not.
     */
    layout?: 'COLUMN' | 'ROW';
  };

export function CheckboxGroup({
  label,
  error,
  readOnly,
  required,
  className,
  children,
  layout = 'COLUMN',
  ...props
}: Readonly<CheckboxGroupProps>): JSX.Element {
  // SENSE-33946: also allow 'li' for EhsFormioOpenChoiceCheckboxGroup and EhsFormioOpenChoiceRadio
  const isAllChildrenCheckboxGroupItem = React.Children.toArray(children).every(
    child =>
      React.isValidElement(child) &&
      (child.type === CheckboxGroupItem || child.type === 'li')
  );
  if (!isAllChildrenCheckboxGroupItem) {
    throw new Error(
      'All children of the CheckboxGroup must be `CheckboxGroupItem` components or `li` elements.'
    );
  }

  const legendId = useRef(_.uniqueId('checkbox-group-legend-'));

  const groupClassnames = ['ehs-checkbox-group', className];
  groupClassnames.push(error ? 'invalid' : '');
  groupClassnames.push(readOnly ? 'read-only' : '');
  groupClassnames.push(required ? 'required' : '');
  groupClassnames.push(layout === 'ROW' ? 'row' : '');
  let errorId = '';
  if (error) {
    errorId = _.uniqueId('ehs-toggle-input-error-');
  }

  return (
    <div
      {...props}
      role="group"
      className={groupClassnames.filter(Boolean).join(' ')}
      aria-labelledby={legendId.current}
    >
      <fieldset
        className="ehs-checkbox-group-fieldset"
        aria-labelledby={legendId.current}
        aria-describedby={errorId}
      >
        <legend id={legendId.current} className="label">
          {label}
          {required && <span aria-hidden="true"> *</span>}
        </legend>

        <div className="ehs-checkbox-list">
          <ul>{children}</ul>
        </div>

        {error && <ValidationErrorMessage id={errorId} error={error} />}
      </fieldset>
    </div>
  );
}

export function CheckboxGroupItem(props: Readonly<CheckboxProps>): JSX.Element {
  return (
    <li className="ehs-checkbox-group-item">
      <Checkbox {...props} />
    </li>
  );
}
